import React, { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { VehicleBuddyIcon } from './VehicleBuddyLogo';
import { 
  Home, Car, Bell, FileText, Wallet, Wrench, AlertTriangle, User, LogOut, Menu, X, Gauge, Scan
} from 'lucide-react';
import { Button } from './ui/button';

const Layout = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navigation = [
    { name: 'Dashboard', href: '/app/dashboard', icon: Home },
    { name: 'Vehicles', href: '/app/vehicles', icon: Car },
    { name: 'Scan Doc', href: '/app/scan', icon: Scan },
    { name: 'Reminders', href: '/app/reminders', icon: Bell },
    { name: 'Expenses', href: '/app/expenses', icon: Wallet },
    { name: 'Mileage', href: '/app/mileage', icon: Gauge },
    { name: 'Maintenance', href: '/app/maintenance', icon: Wrench },
    { name: 'Accidents', href: '/app/accidents', icon: AlertTriangle },
  ];

  const isActive = (href) => {
    return location.pathname === href || location.pathname.startsWith(href + '/');
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 w-full border-b bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/60">
        <div className="container flex h-16 items-center px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2">
            <VehicleBuddyIcon className="h-7 w-7" />
            <Link to="/app/dashboard" className="font-semibold text-lg">
              VehicleBuddy
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex mx-6 flex-1 items-center gap-1">
            {navigation.slice(0, 5).map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                  isActive(item.href)
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
                data-testid={`nav-${item.name.toLowerCase()}`}
              >
                {item.name}
              </Link>
            ))}
          </nav>

          {/* User Menu */}
          <div className="flex items-center gap-2 ml-auto">
            <div className="hidden md:flex items-center gap-2 text-sm">
              <span className="text-muted-foreground">Welcome,</span>
              <span className="font-medium">{user?.name?.split(' ')[0]}</span>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/app/profile')}
              data-testid="profile-button"
            >
              <User className="h-5 w-5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={logout}
              data-testid="logout-button"
            >
              <LogOut className="h-5 w-5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t bg-card">
            <nav className="px-4 py-2 space-y-1">
              {navigation.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                      isActive(item.href)
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                    }`}
                  >
                    <Icon className="h-5 w-5" />
                    {item.name}
                  </Link>
                );
              })}
            </nav>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="container px-4 py-6 sm:px-6 lg:px-8 pb-24 md:pb-6">
        <Outlet />
      </main>

      {/* Mobile Bottom Navigation - Thumb-friendly with safe area */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-50 border-t bg-background/90 backdrop-blur supports-[backdrop-filter]:bg-background/70 pb-safe">
        <nav className="flex items-center justify-around px-2 py-2">
          {navigation.slice(0, 5).map((item, index) => {
            const Icon = item.icon;
            const active = isActive(item.href);
            const isScanButton = item.name === 'Scan Doc';
            
            return (
              <Link
                key={item.name}
                to={item.href}
                className={`flex flex-col items-center gap-1 px-3 py-2.5 rounded-xl touch-target transition-all ${
                  active
                    ? 'text-primary bg-primary/10'
                    : 'text-muted-foreground hover:text-foreground'
                } ${isScanButton ? 'scale-110' : ''}`}
                data-testid={`mobile-nav-${item.name.toLowerCase().replace(' ', '-')}`}
              >
                <Icon className={`${isScanButton ? 'h-6 w-6' : 'h-5 w-5'}`} />
                <span className="text-[10px] font-medium">{item.name}</span>
              </Link>
            );
          })}
        </nav>
      </div>
    </div>
  );
};

export default Layout;
